import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Paper, Box, FormControlLabel, Switch } from '@mui/material';
import { Departement } from '../../models/models';
import { createOrUpdateDepartement, deleteDepartement, setSelectedDepartement } from '../../reduxStore/DepartementSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { useSnackbar } from '../../components/SnackbarProvider';
import MyButton from '../MyButton';
import { ButtonType } from '../../constants/enums';
import SelectDepot from '../Selects/SelectDepot';
import { GeneriqueData } from '../../models/models';

interface DepartementFormProps {
  onCancel: () => void;
}

const DepartementForm: React.FC<DepartementFormProps> = ({ onCancel }) => {
  const initialFormData: Departement = useMemo(() => ({
    nom: '',
    description: '',
    depot_id: null,
    visible: true,
  }), []);

  const { selectedDepartement } = useSelector((state: any) => state.DepartementSlice);
  const [formData, setFormData] = useState<Departement>(initialFormData);
  const [selectedDepot, setSelectedDepot] = useState<GeneriqueData | null>(null);

  useEffect(() => {
    if (selectedDepartement) {
      setFormData({
        id: selectedDepartement.id,
        nom: selectedDepartement.nom,
        description: selectedDepartement.description ?? '',
        depot_id: selectedDepartement.depot_id ?? null,
        visible: selectedDepartement.visible ?? true,
      });
    } else {
      setFormData(initialFormData);
      setSelectedDepot(null);
    }
  }, [initialFormData, selectedDepartement]);

  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDepotChange = (depot: GeneriqueData | null) => {
    setSelectedDepot(depot);
    setFormData((prev) => ({ ...prev, depot_id: depot?.id ?? null }));
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) {
      showSnackbar('Le nom est obligatoire', 'error');
      return;
    }
    try {
      await dispatch(createOrUpdateDepartement(formData)).unwrap();
      showSnackbar('Département enregistré !', 'success');
      dispatch(setSelectedDepartement(null));
      setFormData(initialFormData);
      setSelectedDepot(null);
    } catch (error) {
      showSnackbar((error as string) || "Échec de l'enregistrement", 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedDepartement?.id) return;
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce département ?')) {
      try {
        await dispatch(deleteDepartement(selectedDepartement.id)).unwrap();
        showSnackbar('Département supprimé !', 'success');
        dispatch(setSelectedDepartement(null));
        setFormData(initialFormData);
        onCancel();
      } catch (error) {
        showSnackbar((error as string) || 'Échec de la suppression', 'error');
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      <Box display="grid" gridTemplateColumns="repeat(1, 1fr)" gap={2}>
        <TextField
          name="nom"
          label="Nom"
          value={formData.nom}
          onChange={handleChange}
          fullWidth
          size="small"
          required
        />
        <TextField
          name="description"
          label="Description"
          value={formData.description || ''}
          onChange={handleChange}
          fullWidth
          size="small"
          multiline
          rows={2}
        />
        <SelectDepot
          onDepotChange={handleDepotChange}
          selectedDepot={selectedDepot}
        />
        <FormControlLabel
          control={
            <Switch
              checked={formData.visible ?? true}
              onChange={(e) => setFormData((prev) => ({ ...prev, visible: e.target.checked }))}
            />
          }
          label="Visible"
        />
      </Box>
      <Box mt={2} display="flex" gap={1}>
        <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
          {selectedDepartement ? 'Modifier' : 'Ajouter'}
        </MyButton>
        {selectedDepartement && (
          <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
            Supprimer
          </MyButton>
        )}
        <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={onCancel}>
          Annuler
        </MyButton>
      </Box>
    </Paper>
  );
};

export default DepartementForm;
